import React, { useRef, useState } from "react";
import { Check, Copy, Gauge, Key, Palette, ShieldCheck, Trash2, X } from "lucide-react";
import UsagePanel from "./UsagePanel";
import AppearanceSettings from "./AppearanceSettings";

export default function SettingsModal({
  isOpen,
  activeTab,
  setActiveTab,
  keys,
  webUsage,
  apiUsage,
  loading,
  newKeyData,
  copySuccess,
  setCopySuccess,
  setNewKeyData,
  setIsSettingsOpen,
  createKey,
  deleteKey,
  changePassword,
  createCheckout,
  billingMessage,
  themeMode,
  onThemeModeChange,
}) {
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [confirmKeyId, setConfirmKeyId] = useState(null);
  const [busyKeyId, setBusyKeyId] = useState(null);
  const [keyMessage, setKeyMessage] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [keyName, setKeyName] = useState("");
  const subscriptionSection = useRef(null);

  if (!isOpen) return null;

  const visibleKeys = keys.filter((key) => (key[3] || "manual") === "manual");

  const removeKey = async (id) => {
    if (busyKeyId) return;
    setBusyKeyId(id);
    setKeyMessage("");
    try {
      const result = await deleteKey(id);
      setKeyMessage(result.success ? "" : result.msg || "删除失败，请稍后重试。");
      if (result.success) setConfirmKeyId(null);
    } catch {
      setKeyMessage("删除失败，请检查网络后重试。");
    } finally {
      setBusyKeyId(null);
    }
  };

  const submitPasswordChange = async () => {
    setPasswordMessage(null);
    if (!passwordForm.oldPassword) {
      setPasswordMessage({ success: false, msg: "请输入原密码" });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({ success: false, msg: "新密码至少需要 6 位" });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ success: false, msg: "两次输入的新密码不一致" });
      return;
    }
    const result = await changePassword(passwordForm);
    setPasswordMessage(result);
    if (result.success) {
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    }
  };

  return (
    <>
      <div className="modal-backdrop">
        <section className="settings-modal" role="dialog" aria-modal="true" aria-label="账户设置">
          <header className="modal-header">
            <h2>账户设置</h2>
            <button type="button" className="icon-button" onClick={() => setIsSettingsOpen(false)} aria-label="关闭设置">
              <X size={20} />
            </button>
          </header>
          <div className="modal-body">
            <nav className="settings-nav">
              <button type="button" className={activeTab === "keys" ? "active" : ""} onClick={() => setActiveTab("keys")}>
                <Key size={18} />
                <span>API 密钥</span>
              </button>
              <button type="button" className={activeTab === "billing" ? "active" : ""} onClick={() => setActiveTab("billing")}>
                <Gauge size={18} />
                <span>用量与订阅</span>
              </button>
              <button type="button" className={activeTab === "security" ? "active" : ""} onClick={() => setActiveTab("security")}>
                <ShieldCheck size={18} />
                <span>账号安全</span>
              </button>
              <button type="button" className={activeTab === "appearance" ? "active" : ""} onClick={() => setActiveTab("appearance")}>
                <Palette size={18} /><span>外观</span>
              </button>
            </nav>
            <div className="settings-content">
              {activeTab === "keys" && (
                <div className="settings-section">
                  <div className="section-title-row">
                    <h3>密钥管理</h3>
                    <button type="button" className="primary-small" onClick={() => { setCreatingKey(!creatingKey); setKeyMessage(""); }} disabled={loading}>
                      创建新密钥
                    </button>
                  </div>
                  {creatingKey && <form className="key-create-form" onSubmit={async event => {
                    event.preventDefault();
                    if (!keyName.trim() || loading) return;
                    const result = await createKey(keyName.trim());
                    if (result?.success) { setCreatingKey(false); setKeyName(""); }
                    else setKeyMessage(result?.msg || "创建失败，请重试。");
                  }}><label>密钥名称<input autoFocus required maxLength={64} value={keyName} onChange={event=>setKeyName(event.target.value)} placeholder="例如：工作助手" /></label>
                    <button className="primary-small" type="submit" disabled={loading || !keyName.trim()}>{loading ? "创建中…" : "确认创建"}</button>
                    <button type="button" className="icon-button" aria-label="取消创建密钥" disabled={loading} onClick={()=>setCreatingKey(false)}><X size={16} /></button>
                  </form>}
                  <div className="key-list">
                    {visibleKeys.length === 0 && <p className="muted">暂无密钥</p>}
                    {visibleKeys.map((key) => (
                      <div key={key[4]} className="key-row">
                        <div className="key-identity"><strong>{key[5] || "未命名密钥"}</strong><small>{key[0]}</small></div>
                        <div className="key-row-actions">
                          {confirmKeyId === key[4] ? <>
                            <span className="key-revoke-confirm">删除后立即失效</span>
                            <button type="button" aria-label="确认删除密钥" title="确认删除密钥" disabled={Boolean(busyKeyId)} onClick={() => removeKey(key[4])}><Check size={16} /></button>
                            <button type="button" aria-label="取消删除密钥" title="取消删除密钥" disabled={Boolean(busyKeyId)} onClick={() => setConfirmKeyId(null)}><X size={16} /></button>
                          </> : <button type="button" aria-label="删除密钥" title="删除密钥" disabled={Boolean(busyKeyId)} onClick={() => { setKeyMessage(""); setConfirmKeyId(key[4]); }}><Trash2 size={16} /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {keyMessage && <p role="status" className="key-action-message">{keyMessage}</p>}
                </div>
              )}
              {activeTab === "billing" && (
                <div className="settings-section">
                  <div className="membership-overview">
                    <div>
                      <span>当前订阅</span>
                      <h3>{webUsage?.membership_tier || "free"}</h3>
                    </div>
                  </div>
                  <UsagePanel webUsage={webUsage} apiUsage={apiUsage} onUpgrade={() => subscriptionSection.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })} />
                  {billingMessage?.msg && <p className={`auth-message ${billingMessage.success ? "success" : "error"}`}>{billingMessage.msg}</p>}
                  <div className="recharge-grid" ref={subscriptionSection}>
                    {[
                      { plan: "plus", title: "开通 Plus", desc: "更充裕的使用额度，适合日常对话与图片创作" },
                      { plan: "pro", title: "开通 Pro", desc: "更充足的创作空间，适合持续创作与高频 API 调用" },
                    ].map((item) => (
                      <button key={item.plan} type="button" onClick={() => createCheckout(item.plan)} disabled={loading}>
                        <span>{item.title}</span>
                        <strong>{item.plan.toUpperCase()}</strong>
                        <small>{item.desc}</small>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {activeTab === "appearance" && <AppearanceSettings mode={themeMode || "system"} onChange={onThemeModeChange} />}
              {activeTab === "security" && (
                <div className="settings-section">
                  <div className="section-title-row">
                    <div>
                      <h3>修改密码</h3>
                      <p className="muted security-section-copy">验证原密码后更新。修改成功会保留当前设备，并退出其他登录设备。</p>
                    </div>
                  </div>
                  <div className="security-form">
                    <label>
                      <span>原密码</span>
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={passwordForm.oldPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, oldPassword: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>新密码</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={passwordForm.newPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>确认新密码</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={passwordForm.confirmPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                        onKeyDown={(event) => event.key === "Enter" && submitPasswordChange()}
                      />
                    </label>
                    {passwordMessage?.msg && (
                      <p className={`auth-message ${passwordMessage.success ? "success" : "error"}`}>{passwordMessage.msg}</p>
                    )}
                    <button type="button" className="primary-small security-submit" onClick={submitPasswordChange} disabled={loading}>
                      保存新密码
                    </button>
                  </div>
                  <p className="security-recovery-note">忘记原密码时，请退出登录，在登录页使用绑定手机号验证后重置。</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {newKeyData && (
        <div className="modal-backdrop modal-backdrop-strong">
          <section className="new-key-modal" role="dialog" aria-modal="true" aria-label="新密钥">
            <div className="success-icon">
              <ShieldCheck size={32} />
            </div>
            <h2>密钥已生成</h2>
            <p>请妥善保存。出于安全考量，此密钥仅会显示一次。</p>
            <div className="new-key-value">
              <span>{newKeyData}</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(newKeyData);
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                }}
                aria-label="复制密钥"
              >
                {copySuccess ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <button type="button" className="primary-button" onClick={() => setNewKeyData(null)}>
              确认已保存
            </button>
          </section>
        </div>
      )}
    </>
  );
}
